import { Component, OnInit, Signal, computed, effect, inject, viewChild } from '@angular/core';

import { AssetStateMessageHandlingService } from '@r1-compass-library/services/asset-state-message-handling.service';
import { PlayRecordPlayBuilderComponent } from './play-record-play-builder/play-record-play-builder.component';
import { PlayRecordPlayImporterComponent } from './play-record-play-importer/play-record-play-importer.component';
import { combineLatest, EMPTY, of, switchMap } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';
import { injectParams } from 'ngxtension/inject-params';
import { PlayStore } from '../store/play.store';
import { filterNil } from 'ngxtension/filter-nil';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatMenuModule } from '@angular/material/menu';
import { PlayRecordFormService } from './play-record-form.service';
import { noopFormSaveMutationResult } from '@r1-compass-library/guards/hasUnsavedChanges.guard';
import { rxEffect } from 'ngxtension/rx-effect';
import { iif } from 'rxjs';
import { SaveGuardStore } from '@r1-compass-library/guards/save-guard.store';
import { PlayRecordSelectionParentFormWarning } from '@r1-compass-library/guards/save-guards/play-record-selection.guard';
import { PlayBuilderFormService } from './play-record-play-builder/play-builder-form.service';
import { PlayFileImportForm } from '@r1-compass-library/models/play/play-file-import-form.model';
import { PlayBuilderForm } from './play-record-play-builder/play-builder-form.model';
import {
    PlaySectionName,
    SyncValidationStateSection,
    SyncValidationStateService,
    ValidationService,
    ValidationStateService,
} from '@r1-compass-library/features/validation-sync';
import { PlayFormMap, PlayValidationContext, PlayValidationService } from '../play-validation.service';
import { PlayValidationContextService } from '../play-validation-context.service';
import { Play } from '@r1-compass-library/models';
import { PlayValidationStateService } from '../play-validation-state.service';
import { PlayImporterFormService } from './play-record-play-importer/play-importer-form.service';

@Component({
    selector: 'r1-compass-app-play-record-selection',
    templateUrl: './play-record-selection.component.html',
    styleUrl: './play-record-selection.component.scss',
    imports: [
        PlayRecordPlayBuilderComponent,
        PlayRecordPlayImporterComponent,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        MatMenuModule,
        MatSelectModule,
        MatSlideToggleModule,
        ReactiveFormsModule,
    ],
    providers: [
        PlayRecordFormService,
        PlayRecordPlayBuilderComponent,
        PlayRecordPlayImporterComponent,
        PlayBuilderFormService,
        PlayImporterFormService,
    ],
})
export class PlayRecordSelectionComponent
    implements
        OnInit,
        PlayRecordSelectionParentFormWarning,
        SyncValidationStateSection<PlaySectionName, Play, PlayValidationContext>
{
    readonly playRecordFormService = inject(PlayRecordFormService);
    readonly #formSaveWarningStore = inject(SaveGuardStore);
    readonly #assetStateMessageHandlingService = inject(AssetStateMessageHandlingService);
    readonly #validationService = inject(PlayValidationService);
    readonly #validationContextService = inject(PlayValidationContextService);
    readonly #validationStateService = inject(PlayValidationStateService);
    readonly #syncValidationStateService = inject(SyncValidationStateService);

    playStore = inject(PlayStore);

    playId = injectParams('id');

    shownComponent: Signal<'Builder' | 'Importer' | undefined> = this.playStore.shownComponent;

    private playBuilder = viewChild(PlayRecordPlayBuilderComponent);
    private playImporter = viewChild(PlayRecordPlayImporterComponent);

    canDeactivate$ = combineLatest([toObservable(this.playBuilder), toObservable(this.playImporter)]).pipe(
        switchMap(([playBuilder, playImporter]) => {
            const shownComponent = this.shownComponent();

            const defaultCanDeactivate$ = of({
                dirtied: true,
                valid: false,
            });

            if (shownComponent === 'Builder') {
                return playBuilder?.canDeactivate$ ?? defaultCanDeactivate$;
            } else if (shownComponent === 'Importer') {
                return playImporter?.canDeactivate$ ?? defaultCanDeactivate$;
            } else {
                return defaultCanDeactivate$;
            }
        })
    );

    /**
     * @description Special case since children need to be queried
     */
    handleFormSave$ = rxEffect(
        toObservable(this.shownComponent).pipe(
            switchMap(shown =>
                iif(
                    () => shown === 'Builder',

                    // Has to be handled manually in Builder with `handleFormSaveWarningAfterInit`
                    EMPTY,

                    // But the Importer can handle reactively
                    this.#formSaveWarningStore.saveFormMarkUntouchAndPristinePlayRecordSelection(
                        this.playRecordFormService.playImporterForm
                    )
                )
            )
        )
    );
    saveGuardHandlerFn = this.#formSaveWarningStore.formUnsavedChangesWarningMisc;

    // The save buttons live in the child components, which own the actual logic
    public disableSave = computed<boolean>(() => {
        const shown = this.shownComponent();
        if (shown === 'Builder') return this.playBuilder()?.disableSave() ?? true;
        if (shown === 'Importer') return this.playImporter()?.disableSave() ?? true;
        return true;
    });

    constructor() {
        this.saveGuardHandlerFn({
            canDeactivate$: this.canDeactivate$,
            disableSave: this.disableSave,
        });

        effect(
            () => {
                const shownComponent = this.shownComponent();

                if (shownComponent === 'Importer') {
                    const playImporterForm = this.playRecordFormService.playImporterForm;
                    this.subscribeToValidationState(
                        playImporterForm,
                        'recordSelection',
                        this.#validationService,
                        this.#validationStateService
                    );
                } else if (shownComponent === 'Builder') {
                    const playBuilderForm = this.playRecordFormService.playBuilderForm;
                    this.subscribeToValidationState(
                        playBuilderForm,
                        'recordSelection',
                        this.#validationService,
                        this.#validationStateService
                    );
                }
            },
            { debugName: 'sub to validation state record select' }
        );
    }

    onSave() {
        // Cannot tack on `required` onto the `viewChild` queries to make them not be `undefined`,
        // but between both and the conditional shown component, one will be there.
        // This is for convenience so each block technically returns a mutation fallback for the checker.
        const safeFallback = noopFormSaveMutationResult();

        if (this.shownComponent() === 'Builder') {
            return this.playBuilder()?.onSave() ?? safeFallback;
        } else if (this.shownComponent() === 'Importer') {
            return this.playImporter()?.onSave() ?? safeFallback;
        } else {
            return noopFormSaveMutationResult();
        }
    }

    ngOnInit(): void {
        const playId = this.playId();
        if (playId && playId !== 'new' && this.playStore.play()?.id !== +playId) {
            this.playStore.getPlay({ id: +playId });
        }
        this.playStore.message$.pipe(filterNil()).subscribe(message => {
            this.#assetStateMessageHandlingService.handleMessage(message);
            this.playStore.resetMessage();
        });
    }

    subscribeToValidationState(
        form: FormGroup<PlayBuilderForm> | FormGroup<PlayFileImportForm>,
        sectionName: PlaySectionName,
        validationService: ValidationService<PlaySectionName, Play, PlayFormMap, PlayValidationContext>,
        validationStateService: ValidationStateService<PlaySectionName>
    ) {
        return this.#syncValidationStateService.subToValidationState(
            form,
            sectionName,
            validationService,
            validationStateService,
            this.#validationContextService.context
        );
    }
}
